/**
 * A tool run: a summary line that toggles a step list open, from the
 * prototype's steps card (lines ~92-111 of the design). Running spins the
 * summary icon; done ticks it and folds the list back up on its own, the way
 * the prototype's `applyQuiz`/`runQuiz` do it (`open` follows `running`, but a
 * person can still toggle it either way while it lasts).
 *
 * There is no Undo endpoint yet, so `undoable` renders nothing — only
 * `undone`, which the engine can already set.
 */

import { useEffect, useState } from 'react';
import { fileUrl, type Step } from '../../api';
import { Ph } from '../ui';
import type { CardProps } from './types';

function appsIn(steps: Step[]): string {
  return [...new Set(steps.map((s) => s.app))].join(', ');
}

export function StepsCard({ message }: CardProps) {
  const running = message.body.kind === 'steps' ? message.body.running : false;
  const [open, setOpen] = useState(running);

  // Collapse the moment a run finishes, as the prototype does; a person who
  // opened it while it ran can still close it early, and re-open it after.
  useEffect(() => {
    if (!running) setOpen(false);
  }, [running]);

  if (message.body.kind !== 'steps') return null;
  const { steps, undone } = message.body;
  const apps = appsIn(steps);
  const summary = running
    ? `Working in ${apps || 'your apps'}`
    : steps.length === 1
      ? steps[0]!.label
      : `Ran ${steps.length} steps in ${apps}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <button
        type="button"
        className="bare hov-soft"
        onClick={() => setOpen((o) => !o)}
        style={{
          alignSelf: 'flex-start',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 30,
          padding: '0 10px 0 8px',
          marginLeft: -8,
          borderRadius: 'var(--radius-sm)',
          fontSize: 13.5,
          color: 'var(--text-muted)'
        }}
      >
        {running ? (
          <Ph name="circle-notch" size={15} color="var(--primary)" className="spin" />
        ) : (
          <Ph name="check-circle" fill size={15} color="var(--signal)" />
        )}
        <span style={{ color: 'var(--text-body)' }}>{summary}</span>
        <Ph name={open ? 'caret-up' : 'caret-down'} size={12} />
      </button>

      {open ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            paddingLeft: 10,
            borderLeft: '2px solid var(--border-subtle)'
          }}
        >
          {steps.map((s, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '5px 10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13, color: 'var(--text-muted)' }}>
                <Ph name={s.icon} size={14} color="var(--text-faint)" />
                <span style={{ color: 'var(--text-body)' }}>{s.label}</span>
                <span style={{ color: 'var(--text-faint)' }}>{s.app}</span>
              </div>
              {s.screenshotFileId ? (
                <img
                  src={fileUrl(s.screenshotFileId)}
                  alt=""
                  style={{
                    width: 220,
                    height: 124,
                    borderRadius: 'var(--radius-app-card)',
                    background: 'var(--surface-card)',
                    border: '1px solid var(--border-subtle)',
                    objectFit: 'cover'
                  }}
                />
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {undone ? <span style={{ fontSize: 13, color: 'var(--text-faint)' }}>Undone</span> : null}
    </div>
  );
}
